/**
 * System information — the record's own provenance, and where its last change
 * came from (#1676 provenance + #2444 phase 4).
 *
 * Four facts, each from the source that may answer it:
 *
 *   Created           the row's `created_at` (#1676: timestamps follow record
 *                     visibility, so they sit on the business row)
 *   Last modified     the row's `updated_at`
 *   Last changed by   `core.record_last_change` — the actor's NAME and, when the
 *                     act was delegated, the principal it was on behalf of
 *   Last changed via  the same projection — the source (Console, MCP, API
 *                     integration…), the client's label, and a badge that says
 *                     whether that source was VERIFIED, DECLARED or INFERRED
 *
 * ── what is deliberately NOT here ──────────────────────────────────────────
 * The row's four actor columns (`created_by`, `updated_by`, and their
 * `_on_behalf_of` twins) are raw uuids. #1676 held their classification until
 * a governed person/app-user resolver lands for them, and a uuid is never a
 * name (#1676 ruling 5) — so "Created by" is not drawn from them. The latest
 * change's actor comes from the projection, which resolves it server-side.
 *
 * States, and each says what it is: loading, a refused or failed read of the
 * projection ("not the same as there being none"), no recorded change this
 * caller may see, and the populated facts. The timestamps render even when the
 * projection cannot answer, because they come from the row.
 */
import { FactList, PageSection, Pill, type Fact } from '@solum/ui'
import { trustPresentation } from '@solum/sdk'
import { queryState, unresolvedText } from '../data/QueryBoundary'
import { useUserFormat } from '../data/userFormat'
import { useRecordLastChange } from '../audit/auditQueries'
import { actorWords, formatWhen } from '../audit/auditWords'

export function RecordSystemInfo({ entity, id, row }: {
  entity: string
  id: string
  row: Record<string, unknown> | null | undefined
}) {
  const fmt = useUserFormat()
  const zone = { locale: fmt.prefs.locale, timeZone: fmt.prefs.timezone }
  const last = useRecordLastChange(entity, id)
  /* OPTIONAL, not required: "no row" is the projection's honest answer for a
     record with no change this caller may see, not a failed read. */
  const { kind } = queryState([], [last])
  const change = last.data ?? null
  const at = (value: unknown) => (typeof value === 'string' && value ? formatWhen(value, zone) : null)

  const unresolved = kind === 'ready' ? null : unresolvedText(kind, 'where this record was last changed')
  const trust = change ? trustPresentation(change.viaTrust) : null

  const facts: Fact[] = [
    { key: 'created', label: 'Created', value: at(row?.created_at), testId: 'system-created' },
    { key: 'modified', label: 'Last modified', value: at(row?.updated_at), testId: 'system-modified' },
    {
      key: 'changed-by',
      label: 'Last changed by',
      value: change ? actorWords(change.actorLabel, change.onBehalfOfLabel) : null,
      hint: unresolved ?? (change ? at(change.changedAt) : 'No recorded change you can see.'),
      testId: 'system-changed-by',
    },
    {
      key: 'changed-via',
      label: 'Last changed via',
      value: change && trust
        ? <>
            {change.viaSourceLabel}{change.viaClientLabel ? ` · ${change.viaClientLabel}` : ''}{' '}
            <Pill tone={trust.tone} title={trust.description} data-trust={change.viaTrust ?? 'unknown'}
              data-testid="system-trust">{trust.label}</Pill>
          </>
        : null,
      hint: unresolved ?? (change && trust ? trust.description : undefined),
      testId: 'system-changed-via',
    },
  ]

  return (
    <PageSection title="System information" headingLevel={2} testId="record-system-info">
      <FactList facts={facts} label="System information" />
    </PageSection>
  )
}
